import React from 'react';
import {
  CContainer,
  CListGroup,
  CListGroupItem,
  CCard,
  CCardBody,
  CCardText,
  CFormSelect,
  CFormInput,
} from '@coreui/react-pro';
import {
  Map,
  useMap,
  MapControl,
  ControlPosition,
  useMapsLibrary,
  AdvancedMarker,
} from '@vis.gl/react-google-maps';
import MapLoadingSpinner from '../shared/MapLoadingSpinner';
import MyFieldsAgvanceFieldBoundary from './MyFieldsAgvanceFieldBoundary';
import NetOfFreightLocationMarker from './NetOfFreightLocationMarker';
import styles from './Mapping.module.css';
import {
  initNetOfFreightInterface,
  userSelectedNetOfFreightCommodity,
  userSelectedNetOfFreightFarmField,
  userEnteredPerMileCost,
  userEnteredTruckBaseRate,
  setUserDraggedOrigin,
} from './mapping-actions';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

const NetOfFreight = () => {

  const dispatch = useDispatch();

  const map = useMap();

  const {
    sidebarVisible,
    sidebarUnfoldable,
  } = useSelector(state => state.userSettings);

  const {
    fetchingMappedFarmFieldsList,
    netOfFreightSelectedCommodity,
    netOfFreightCommoditiesDropDownListDataSource,
    mappedFarmFieldsList,
    selectedNetOfFreightFarmField,
    fetchingNetOfFreightLocations,
    netOfFreightLocations,
    netOfFreightPerMileCost,
    netOfFreightTruckBaseRate,
    userDraggedOrigin,
  } = useSelector(state => state.mapping);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  const routesLibrary = useMapsLibrary('routes');

  const [directionsService, setDirectionsService] = React.useState(null);

  const [directionsRenderer1, setDirectionsRenderer1] = React.useState(null);

  const [directionsRenderer2, setDirectionsRenderer2] = React.useState(null);

  const [directionsRenderer3, setDirectionsRenderer3] = React.useState(null);

  const [location1, setLocation1] = React.useState(null);

  const [location2, setLocation2] = React.useState(null);

  const [location3, setLocation3] = React.useState(null);

  React.useEffect(() => {
    dispatch(initNetOfFreightInterface());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize)
  }, []);

  React.useEffect(() => {
    if (!routesLibrary || !map) {
      return;
    }
    setDirectionsService(new routesLibrary.DirectionsService());
    setDirectionsRenderer1(new routesLibrary.DirectionsRenderer({map}));
    setDirectionsRenderer2(new routesLibrary.DirectionsRenderer({map}));
    setDirectionsRenderer3(new routesLibrary.DirectionsRenderer({map}));
  }, [routesLibrary, map]);

  React.useEffect(() => {
    if (!directionsService || !directionsRenderer1 || !directionsRenderer2 || !directionsRenderer3 || netOfFreightLocations.length === 0 || selectedNetOfFreightFarmField === null) {
      return;
    }
    var count = 1;
    netOfFreightLocations.forEach(location => {
      directionsService.route({
        origin: {
          lat: userDraggedOrigin !== null ? userDraggedOrigin.latitude : selectedNetOfFreightFarmField.Latitude,
          lng: userDraggedOrigin !== null ? userDraggedOrigin.longitude : selectedNetOfFreightFarmField.Longitude,
        },
        destination: {
          lat: location.lat,
          lng: location.lng,
        },
        travelMode: 'DRIVING',
        provideRouteAlternatives: false,
        avoidHighways: false,
      }).then(response => {
        if (count === 1) {
          directionsRenderer1.setDirections(response);
          setLocation1({
            location: location, 
            directions: response,
          });
        }
        else if (count === 2) {
          directionsRenderer2.setDirections(response);
          setLocation2({
            location: location, 
            directions: response,
          });
        }
        else {
          directionsRenderer3.setDirections(response);
          setLocation3({
            location: location, 
            directions: response,
          });
        }
        ++count;
      });
    });
  }, [directionsService, directionsRenderer1, directionsRenderer2, directionsRenderer3, netOfFreightLocations, selectedNetOfFreightFarmField, userDraggedOrigin]);

  const height = windowHeight - 64;

  var width = windowWidth;

  if (sidebarUnfoldable === true && sidebarVisible === true) {
    width -= 64;
  }
  else if (sidebarUnfoldable === false && sidebarVisible === true) {
    width -= 256;
  }

  return (    
    <div style={{flexDirection: 'row', display: 'flex',}}>
      <div style={{
        width: 300,
        height: height,
        overflowY: 'auto', 
      }}>
        <CContainer fluid>
          <CCard>
            <CCardBody>
              <CCardText>
                To determine the best delivery location based on farm location & price: Use the Net of Freight tool to compare road miles to surrounding elevators and compare the cash price after your implied freight expense is removed.
                Select a Commodity and click on a Field Name below to begin.
              </CCardText>
              <CFormInput
                floatingLabel='Per Mile Cost'
                className='mb-3'
                value={netOfFreightPerMileCost}
                onChange={(e) => dispatch(userEnteredPerMileCost(e.target.value))}
              />
              <CFormInput
                floatingLabel='Truck Base Rate'
                className='mb-3'
                value={netOfFreightTruckBaseRate}
                onChange={(e) => dispatch(userEnteredTruckBaseRate(e.target.value))}
              />
              <CFormSelect
                floatingLabel='Select Commodity'
                className='mb-3'
                style={{width: '100%', lineHeight: 4,}}
                value={netOfFreightSelectedCommodity}
                options={netOfFreightCommoditiesDropDownListDataSource}
                onChange={(e) => dispatch(userSelectedNetOfFreightCommodity(e.target.value))} />
              <CListGroup>
                {
                  mappedFarmFieldsList.map((field, index) => {
                    return (
                      <CListGroupItem 
                        key={field.FieldId + index}
                        active={selectedNetOfFreightFarmField?.fieldGUID === field.fieldGUID}
                        style={{cursor: 'pointer',}}
                        onClick={() => dispatch(userSelectedNetOfFreightFarmField(field))}>
                        {field.FieldId}
                      </CListGroupItem>
                    );
                  })
                }
              </CListGroup>
            </CCardBody>
          </CCard>
        </CContainer>
      </div>
      <div style={{flexDirection: 'column', display: 'flex',}}>
        <div
          style={{
            width: width - 300,
            height: height,
          }}>
          <Map
            mapId='NET_OF_FREIGHT'
            defaultCenter={{
              lng: -88.257311,
              lat: 40.112519,
            }} 
            defaultZoom={parseInt(process.env.REACT_APP_DEFAULT_MAP_ZOOM)}
            fullscreenControl={false}
            mapTypeControl={true}
            streetViewControl={false}
            mapTypeId='hybrid'
          >
            {
              fetchingMappedFarmFieldsList === true ? (
                <MapControl position={ControlPosition.CENTER}>
                  <MapLoadingSpinner loadingText='Fetching Farm Fields' />
                </MapControl>
              ) : null
            }
            {
              fetchingNetOfFreightLocations === true ? (
                <MapControl position={ControlPosition.CENTER}>
                  <MapLoadingSpinner loadingText='Fetching Nearest Locations' />
                </MapControl>
              ) : null
            }
            {
              selectedNetOfFreightFarmField?.GoogleMapFieldPolygons.map((polygon, index) => (
                <MyFieldsAgvanceFieldBoundary farmFieldPolygon={polygon} key={index} fillColor={netOfFreightSelectedCommodity === 'Corn' ? 'yellow' : 'green'} />
              ))
            }
            {
              location1 !== null ? (
                <AdvancedMarker
                  clickable={false}
                  draggable={true}
                  onDragEnd={(e) => dispatch(setUserDraggedOrigin({
                    latitude: e.latLng.lat(),
                    longitude: e.latLng.lng(),
                  }))}
                  zIndex={10000}
                  position={{
                    lat: location1.directions.routes[0].legs[0].start_location.lat(),
                    lng: location1.directions.routes[0].legs[0].start_location.lng(),
                  }}
                />
              ) : null
            }
            {
              location1 !== null ? (
                <NetOfFreightLocationMarker location={location1} />
              ) : null
            }
            {
              location2 !== null ? (
                <NetOfFreightLocationMarker location={location2} />
              ) : null
            }
            {
              location3 !== null ? (
                <NetOfFreightLocationMarker location={location3} />
              ) : null
            }
          </Map>
          <div className={styles.controlPanel}>
            <h3>Route Origin</h3>
            <p>
              You can change the route origin by dragging the red origin marker on the map.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NetOfFreight;
